const en = {
  // Shared
  breadcrumb: "Breadcrumb",
  navAria: "Admin",
  navSectionsAria: "Admin sections",
  reset: "Reset",
  any: "Any",
  anyone: "Anyone",
  suspended: "Suspended",
  suspendedReason: "Suspended: {reason}",
  reasonRequired: "A reason is required.",
  reasonAuditLabel: "Reason (recorded in the audit log)",
  reinstate: "Reinstate",
  system: "system",
  unknown: "unknown",
  free: "Free",
  perMonth: "{price}/mo",
  perYear: "{price}/yr",
  healthOk: "OK",
  healthWarn: "Warning",
  healthError: "Error",
  slug: "Slug",
  currency: "Currency",
  sortOrder: "Sort order",
  message: "Message",
  sending: "Sending…",
  scope: "Scope",
  scope_platform: "Platform",
  scope_store: "Store",
  colStore: "Store",
  colOwner: "Owner",
  colPlan: "Plan",
  colRole: "Role",
  colUser: "User",
  colWhen: "When",
  colActor: "Actor",
  colAction: "Action",
  colTarget: "Target",
  colDetails: "Details",
  colOrders: "Orders",
  colProvider: "Provider",
  colAttempts: "Attempts",
  colLastError: "Last error",
  colSubject: "Subject",
  products: "Products",
  customers: "Customers",
  market: "Market",
  joined: "Joined",
  lastSignIn: "Last sign-in",
  platformRole: "Platform role",
  noAuditEntries: "No audit entries.",

  // Overview
  overviewTitle: "Platform overview",
  overviewDesc: "Operational snapshot of the whole platform.",
  mUsers: "Users",
  mUsersSub: "+{d30} last 30 days · {suspended} suspended",
  mActiveMerchants: "Active merchants",
  mActiveMerchantsSub: "Owners with a published store",
  mStores: "Stores",
  mLast30: "+{d30} last 30 days",
  mSuspendedStores: "Suspended stores",
  mErrors24h: "Errors (24h)",
  revenueTitle: "Platform subscription revenue",
  revenueDesc: "Paid platform invoices only (SaaS fees).",
  revenueEmpty: "No paid platform invoices yet.",
  colLast30: "Last 30 days",
  colAllTime: "All time",
  gmvTitle: "Merchant sales (GMV), last 30 days",
  gmvDesc: "Paid, non-test orders net of refunds, by store currency.",
  gmvWarnStrong: "Merchant sales are not platform revenue.",
  gmvWarnBody: "These funds settle directly to merchants’ own payment accounts.",
  gmvEmpty: "No paid merchant orders in the last 30 days.",
  colNetSales: "Net sales",
  planDistTitle: "Plan distribution",
  planDistDesc: "Active, trialing and past-due subscriptions.",
  plansLink: "Plans →",
  supportQueueTitle: "Support queue",
  openQueueLink: "Open queue →",
  mOpenPending: "Open / pending",
  storageInUse: "Media storage in use: {size}",
  connTitle: "Merchant payment connections",
  connDesc: "Connection status only — no credentials.",
  catalogueLink: "Catalogue →",
  connEmpty: "No merchant payment connections yet.",
  recentStoresTitle: "Recent stores",
  allStoresLink: "All stores →",
  noStoresYet: "No stores yet.",

  // Users
  usersMatching: "{n} matching accounts",
  usersMatching_one: "{n} matching account",
  usersSearchPlaceholder: "Name or email",
  colStoresOwned: "Stores owned",
  unverified: "unverified",
  noUsersMatch: "No users match these filters.",
  metaUser: "User",
  suspendUser: "Suspend user",
  suspendUserTitle: "Suspend {email}?",
  suspendUserBody: "They are signed out everywhere and can’t sign in until reinstated. Their stores stay as they are — suspend stores separately if needed.",
  reinstateUser: "Reinstate user",
  reinstateUserTitle: "Reinstate {email}?",
  reinstateUserBody: "They’ll be able to sign in again.",
  revokeSessions: "Revoke sessions",
  revokeSessionsCount: "Revoke sessions ({count})",
  revokeSessionsTitle: "Revoke all sessions?",
  revokeSessionsBody: "The user will be signed out on every device.",
  selfAccount: "This is your own account. Status and role changes must be made by another administrator.",
  profile: "Profile",
  verified: "Verified",
  notVerified: "Not verified",
  locale: "Locale",
  sessions: "Sessions",
  sessionsActive: "{n} active",
  roleChangeHint: "Changing the role signs the user out.",
  roleSuperOnly: "Only superadmins can change platform roles.",
  newRole: "New role",
  changeRole: "Change role",
  changeRoleConfirm: "Change this user’s platform role? They will be signed out.",
  subscription: "Subscription",
  billing: "Billing",
  billingStripe: "Stripe (platform billing)",
  billingNone: "None / assigned",
  trialEnds: "Trial ends",
  periodEnds: "Period ends",
  noSubscription: "No subscription record yet (default plan applies).",
  assignPlanLabel: "Assign plan (no payment taken)",
  planInactive: "{name} (inactive)",
  assignPlan: "Assign plan",
  assignAs: "As",
  assignAsActive: "active",
  assignAsTrial: "trial",
  assignPlanConfirm: "Assign this plan without taking payment?",
  storesRoles: "Stores & roles",
  notMember: "Not a member of any store.",
  recentActivity: "Recent activity",
  recentActivityDesc: "Audit entries where this user was the actor.",
  noActivity: "No recorded activity.",
  userSuspended: "User suspended and signed out.",
  userReinstated: "User reinstated.",
  roleUpdated: "Platform role updated. The user’s sessions were revoked.",
  planAssigned: "Plan assigned.",
  sessionsRevoked: "All sessions revoked.",

  // Stores
  storesMatching: "{n} matching stores",
  storesMatching_one: "{n} matching store",
  storesSearchPlaceholder: "Name or slug",
  colActiveProducts: "Active products",
  colVerifiedDomains: "Verified domains",
  noStoresMatch: "No stores match these filters.",
  metaStore: "Store",
  viewStorefront: "View storefront ↗",
  reinstateStore: "Reinstate store",
  reinstateStoreTitle: "Reinstate {name}?",
  reinstateStoreBody: "The store returns to draft. The merchant decides when to publish it again.",
  suspendStore: "Suspend store",
  suspendStoreTitle: "Suspend {name}?",
  suspendStoreBody: "The storefront goes offline and checkout stops. Data, orders and payment connections are kept untouched.",
  identity: "Identity",
  publicUrl: "Public URL",
  businessType: "Business type",
  theme: "Theme",
  ownerPlan: "Owner & plan",
  ownerStatus: "Owner status",
  effectivePlan: "Effective plan",
  contactEmail: "Contact email",
  domains: "Domains",
  platformAddressOnly: "Platform address only.",
  primary: "primary",
  paymentConnections: "Payment connections",
  paymentConnectionsDesc: "Status only. Credentials are never loaded or displayed in the admin area.",
  colCheckout: "Checkout",
  colMode: "Mode",
  mode_test: "Test",
  mode_live: "Live",
  colLastVerified: "Last verified",
  off: "Off",
  noPaymentMethods: "No payment methods set up.",
  storeAuditDesc: "Most recent 25 entries for this store.",
  storeSuspended: "Store suspended.",
  storeReinstated: "Store reinstated as a draft — the merchant can publish it again.",

  // Plans
  plansDesc: "Platform subscription plans. Lowering limits never deletes merchant data — merchants above a limit keep their data but can’t add more.",
  newPlan: "New plan",
  crumbNew: "New",
  metaEditPlan: "Edit plan",
  colFlags: "Flags",
  colSubscribers: "Subscribers",
  trialBadge: "{n}-day trial",
  limitsSummary: "{stores} stores · {products} products · {staff} staff · {domains} domains · {storage}",
  flagDefault: "default",
  flagActive: "active",
  flagInactive: "inactive",
  flagPublic: "public",
  flagHidden: "hidden",
  flagStripePrice: "Stripe price",
  subscribersCount: "{n} subscribers",
  subscribersCount_one: "{n} subscriber",
  planCreated: "Plan created.",
  planChangesInfo: "Changes apply to all {n} current subscribers. Lowering limits never deletes merchant data; price changes don’t alter existing Stripe subscriptions until they are changed in Stripe.",
  planChangesInfo_one: "Changes apply to the {n} current subscriber. Lowering limits never deletes merchant data; price changes don’t alter existing Stripe subscriptions until they are changed in Stripe.",
  planSaved: "Plan saved. Existing merchant data is unaffected by limit changes.",
  planCode: "Code",
  planCodeHint: "Lowercase letters, numbers, - and _. Used in URLs and billing.",
  prices: "Prices",
  monthlyPrice: "Monthly price",
  monthlyPriceHint: "Decimal, e.g. 29.00",
  yearlyPrice: "Yearly price",
  trialDays: "Trial days",
  limits: "Limits",
  limitsHint: "Lowering a limit never deletes merchant data — merchants above it keep what they have but can’t add more.",
  limitStores: "Stores",
  limitProducts: "Products / store",
  limitStaff: "Staff / store",
  limitDomains: "Custom domains",
  limitStorage: "Storage (MB)",
  features: "Features",
  feature_discounts: "Discount codes",
  feature_digital_products: "Digital products",
  feature_advanced_analytics: "Advanced analytics",
  feature_abandoned_cart: "Abandoned-cart reminders",
  feature_reviews: "Product reviews with moderation",
  feature_custom_domains: "Custom domains",
  feature_team: "Team accounts & roles",
  feature_all_themes: "All themes, including premium",
  feature_priority_support: "Priority support",
  visibilityBilling: "Visibility & billing",
  public: "Public",
  publicHint: "Shown on the pricing page and in merchants’ plan picker.",
  defaultPlanActiveHint: "The default plan must stay active.",
  inactivePlanHint: "Inactive plans can’t be chosen; existing subscribers keep them.",
  stripePriceMonthly: "Stripe price ID (monthly)",
  stripePriceYearly: "Stripe price ID (yearly)",
  stripePriceHint: "price_… from the platform Stripe account. Leave empty to use inline pricing.",
  createPlan: "Create plan",
  savePlan: "Save plan",

  // Payments
  paymentsTitle: "Payment integrations",
  paymentsDesc: "Which payment integrations the platform offers to merchants, and how merchants are using them.",
  howTitle: "How payment availability works",
  step1Title: "1 · Available on platform",
  step1Body: "The toggle on this page. Controls whether merchants may set up the integration. Restricting requires a reason that merchants see.",
  step2Title: "2 · Operational config",
  step2Body: "Platform-level settings some methods need, e.g. Stripe Connect OAuth app and its webhook secret (environment variables). Without Connect, merchants can still use their own Stripe API keys.",
  step3Title: "3 · Merchant account connection",
  step3Body: "Each merchant connects their own provider account. Funds settle to the merchant, never to the platform.",
  step4Title: "4 · Store-level activation",
  step4Body: "The merchant turns a connected method on or off for their checkout.",
  restrictWarning: "Restricting a provider never moves, reroutes or captures any merchant’s payments, and never connects or alters a merchant’s account. It only limits use of that integration going forward.",
  catalogueTitle: "Provider catalogue",
  colImplemented: "Implemented",
  colOperational: "Operational config",
  colConnected: "Connected",
  colEnabledCheckout: "Enabled at checkout",
  colErrors: "Errors",
  colAvailability: "Platform availability",
  providerKind_online: "Online",
  providerKind_offline: "Offline",
  method_oauth: "OAuth",
  method_api_keys: "API keys",
  method_offline: "offline",
  notYetSupported: "Not yet supported",
  connectOauth: "Connect OAuth:",
  connectWebhook: "Connect webhook:",
  configured: "configured",
  notConfigured: "not configured",
  configuredCap: "Configured",
  notConfiguredCap: "Not configured",
  noneNeeded: "None needed",
  perMerchant: "Per merchant (own credentials)",
  available: "Available",
  restricted: "Restricted",
  platformBilling: "Platform billing",
  platformBillingDesc: "Storelancer’s own SaaS subscription billing.",
  billingApiKey: "Billing API key",
  billingKeyHint: "Paid plans can only be assigned by admins until {env} is set.",
  billingWebhook: "Billing webhook",
  secretConfigured: "Secret configured",
  secretNotConfigured: "Secret not configured",
  webhookEndpoint: "Webhook endpoint",
  merchantPaymentsNote: "Merchant store payments always use each merchant’s own connected provider account.",
  restrict: "Restrict…",
  restrictionReason: "Restriction reason (shown to merchants)",
  restrictProvider: "Restrict provider",
  makeAvailable: "Make available",
  liftRestriction: "Lift restriction",
  providerAvailable: "Provider available on the platform.",
  providerRestricted: "Provider restricted. Existing merchant payments were not rerouted.",

  // Support
  ticketsByStatus: "{n} tickets · {status}",
  ticketsByStatus_one: "{n} ticket · {status}",
  ticketsOpenPending: "{n} open or pending tickets, most urgent first",
  ticketsOpenPending_one: "{n} open or pending ticket, most urgent first",
  statusOpenPending: "Open + pending",
  pendingAwaiting: "Pending (awaiting requester)",
  ticketStatus_open: "Open",
  ticketStatus_pending: "Pending",
  ticketStatus_resolved: "Resolved",
  ticketStatus_closed: "Closed",
  priority: "Priority",
  priority_low: "Low",
  priority_normal: "Normal",
  priority_high: "High",
  priority_urgent: "Urgent",
  category_general: "General question",
  category_billing: "Plan & billing",
  category_payments: "Payments & payouts",
  category_domains: "Domains",
  category_technical: "Technical problem",
  category_account: "Account & sign-in",
  assignee: "Assignee",
  assignedToMe: "Assigned to me",
  unassigned: "Unassigned",
  requester: "Requester",
  colCategory: "Category",
  guest: "guest",
  staff: "Staff",
  queueEmpty: "The queue is empty.",
  ticket: "Ticket",
  ticketDesc: "#{number} · {category} · opened {date}",
  conversation: "Conversation",
  badgeInternalNote: "internal note",
  badgeStaffReply: "staff reply",
  badgeRequester: "requester",
  respond: "Respond",
  account: "Account",
  viewUser: "View user",
  guestNoAccount: "Guest (no account)",
  replyToRequester: "Reply to requester",
  addInternalNote: "Add internal note",
  replyHint: "Replies are emailed to the requester. Internal notes are visible to staff only.",
  updateTicket: "Update ticket",
  writeMessageFirst: "Write a message first.",
  internalNoteAdded: "Internal note added.",
  replySent: "Reply sent and emailed to the requester.",
  ticketUpdated: "Ticket updated.",

  // Content
  contentTitle: "Platform content",
  contentDesc: "Announcements, FAQs, help articles and legal pages shown on the public site. Legal pages here replace the built-in templates (slugs: terms, privacy, cookies, acceptable-use).",
  contentDeleted: "Content deleted.",
  tab_announcement: "Announcements",
  tab_faq: "FAQs",
  tab_doc: "Docs",
  tab_legal: "Legal",
  kind_announcement: "Announcement",
  kind_faq: "FAQ",
  kind_doc: "Doc article",
  kind_legal: "Legal page",
  colState: "State",
  colOrder: "Order",
  publicLink: "Public link",
  viewExternal: "View ↗",
  noContent: "No content yet.",
  newContent: "New content",
  metaEditContent: "Edit content",
  deleteContentTitle: "Delete “{title}”?",
  deleteLegalBody: "The public page falls back to the built-in template.",
  slugHint: "Legal: terms, privacy, cookies, acceptable-use",
  body: "Body",
  bodyHint: "Plain text. Use “## ” for headings, “- ” for bullet lists and blank lines between paragraphs. HTML is not rendered.",
  contentCreated: "Created “{title}”.",

  // Health
  healthChecked: "Checked {time}. Configuration values and secrets are never shown here.",
  colCheck: "Check",
  colDetail: "Detail",
  eventsTitle: "Recent system events",
  eventsDesc: "Last 50 events from workers, webhooks and the app.",
  colLevel: "Level",
  colSource: "Source",
  noEvents: "No system events recorded.",
  failedJobsTitle: "Failed background jobs",
  failedJobsDesc: "Jobs that exhausted their retries.",
  colFinished: "Finished",
  noFailedJobs: "No failed jobs.",

  // Audit
  auditDesc: "Sensitive platform and store actions, newest first.",
  auditSearchLabel: "Action, actor email or target id",
  auditSearchPlaceholder: "e.g. {example}",
  storeLink: "store",
  noAuditMatch: "No audit entries match.",
  paginationAria: "Pagination",
  pageN: "Page {page}",
  newer: "Newer",
  older: "Older",

  // Emails
  emailsDesc: "Latest 100 outgoing emails — metadata only. Message bodies are never shown because they can contain password-reset or download links.",
  colQueued: "Queued",
  colTo: "To",
  colTemplate: "Template",
  loggedNoSmtp: "Logged (no SMTP)",
  noEmails: "No emails yet.",
};

export default en;
